import type { Products } from "../../../api/types.ts";
import type { SubscriptionBase } from "../../../api/session/subscription.ts";
import { differenceInDays } from "@sg-protect/components";

// Mirrors sg-home's StatusBanners renewal-window threshold, ported for parity.
const SUBSCRIPTION_RENEWAL_THRESHOLD_DAYS = 90;

export type ExpirationStatus =
    | "expiring"
    | "expiring-trial"
    | "expired-single"
    | "expired-trial-single"
    | "expired-all"
    | "expired-some";

interface ExpiringTrialResult {
    status: "expiring-trial";
    // The subscription the countdown reads its end date from, carried out of the filtering that
    // selected it so callers never re-derive it from a wider list.
    trial: SubscriptionBase;
}

interface ExpirationStatusResult {
    status: Exclude<ExpirationStatus, "expiring-trial">;
}

export type ExpirationResult = ExpiringTrialResult | ExpirationStatusResult;

// protect.subscription and migrate.subscription can resolve to the same underlying subscription (e.g. a
// combined ShareGate trial granting both products' access add-ons) -- the backend already computes
// that comparison (it owns the raw subscriptionId, which never reaches the client), so dedupe on its
// result instead of re-deriving it here.
export function getBannerSubscriptions(products: Products | undefined): SubscriptionBase[] {
    // Narrows against the raw union (ProtectSubscription | MigrateSubscription), not SubscriptionBase:
    // MigrateSubscription is missing hasMigrationProjectAddOn from SubscriptionBase's Pick, so "is
    // SubscriptionBase" wouldn't satisfy TS's predicate-assignability check even though the return
    // itself (a wider shape into a narrower one) is fine.
    const subscriptions = [products?.protect.subscription, products?.migrate.subscription].filter(
        (s): s is NonNullable<typeof s> => s != null
    );

    if (subscriptions.length === 2 && products?.isSharedSubscription) {
        return [subscriptions[0]];
    }

    return subscriptions;
}

// The expiring banner takes precedence over the expired banner; the two never show at the same time.
export function resolveExpirationStatus(subscriptions: SubscriptionBase[], now: number): ExpirationResult | null {
    // Computed on the full set (partner-granted access included) so an active partner subscription
    // still suppresses a leftover expired trial. "Real" = a non-trial sub in an actionable state.
    const hasRealSubscription = subscriptions.some(s => !s.isTrial && (s.state === "Active" || s.state === "Expired"));

    // A partner perk (partner-granted access with no paid subscription backing it) is a free
    // grant, not an admin-renewable product: it gates trial suppression above but never headlines its
    // own expired/expiring banner. Once a paid subscription exists alongside it, isPartnerPerk is
    // false and it contributes like any other subscription.
    const contributing = subscriptions.filter(s => !s.isPartnerPerk);

    // An expired trial is not actionable once a real subscription exists, whether that real sub is
    // still active or has itself expired.
    const relevant = contributing.filter(s => !(hasRealSubscription && s.isTrial && s.state === "Expired"));

    // Only Active/Expired subscriptions are actionable for the expired banner.
    const actionable = relevant.filter(s => s.state === "Active" || s.state === "Expired");

    // A lone trial keeps its trial-specific copy; every other shape uses the generic aggregate
    // messages, since two subscriptions can't share one countdown. The SKU is irrelevant here --
    // the banner copy never names a product, so a Protect-only or Migrate-only trial reads the same
    // as a combined one.
    const loneTrial = actionable.length === 1 && actionable[0].isTrial ? actionable[0] : null;

    // isValid already encodes "not blocked and endDate hasn't passed" as a single backend-computed
    // flag, so this correctly excludes a blocked subscription from also being counted as "expiring
    // soon" and never needs its own past-due guard.
    const hasExpiring = actionable.some(
        s => s.isValid && s.endDate != null && differenceInDays(s.endDate, now) <= SUBSCRIPTION_RENEWAL_THRESHOLD_DAYS
    );

    if (hasExpiring) {
        return loneTrial ? { status: "expiring-trial", trial: loneTrial } : { status: "expiring" };
    }

    const expiredCount = actionable.filter(s => s.state === "Expired").length;

    if (expiredCount === 0) {
        return null;
    }

    if (expiredCount < actionable.length) {
        return { status: "expired-some" };
    }

    if (actionable.length === 1) {
        return { status: loneTrial ? "expired-trial-single" : "expired-single" };
    }

    return { status: "expired-all" };
}

// Day count remaining on the expiring trial (clamped at 0).
export function getExpiringTrialDaysLeft(trial: SubscriptionBase, now: number): number {
    return trial.endDate != null ? Math.max(differenceInDays(trial.endDate, now), 0) : 0;
}

export type SubscriptionChannel = "reseller" | "direct" | "none";

// Which channel a workspace acts on its subscription through. Non-admins get none of them, since
// they can't act on the subscription at all; reseller-managed workspaces always go through their
// reseller rather than our own purchase/renew flow, even for admins.
export function resolveSubscriptionChannel({
    isProvidedByReseller,
    isWorkspaceAdmin
}: {
    isProvidedByReseller: boolean;
    isWorkspaceAdmin: boolean;
}): SubscriptionChannel {
    if (!isWorkspaceAdmin) {
        return "none";
    }

    return isProvidedByReseller ? "reseller" : "direct";
}
